import { DESCRIPTION_ENUM, SHIPPO_LABEL_URL } from '@/shared/config';
import { generateFullAddress } from '@/shared/lib';
import axios from 'axios';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { TicketOutboundProps } from './ticketOutbound.types';

/**
 * Derives everything the RMA outbound panel renders from the ticket: one row per sender device plus the shipping and returning address rows, notification emails, clinic notes and labels. It downloads the label PDFs, resolves each device's label index from the shipment devices, moves a generic Other reason to the end of its reason list, formats dateOfEvent and maps isReceived to Delivered or Pending; with no ticket it yields empty values.
 *
 * @param data - The RMA ticket whose outbound side is displayed.
 * @returns Label PDFs, device rows, addresses, notes and the navigation handler.
 */
const useTicketOutbound = ({ data }: Pick<TicketOutboundProps, 'data'>) => {
  const navigate = useNavigate();

  const [filePdf, setFilePdf] = useState([]);

  /**
   * Downloads the Shippo label PDFs of an RMA ticket's shipments so the outbound panel can offer them. Each label URL is rewritten onto the configured Shippo proxy with the original host sent as a target header, all requests are fetched as arraybuffers in parallel and the buffers land in state; failures are only logged.
   *
   * @param file - Shipment labels whose transactions carry the label URLs.
   * @returns Resolves once the PDFs have been stored.
   */
  const formatFilePDF = async (file: any): Promise<any> => {
    const promise: any = [];
    file?.forEach(({ transaction }: any) => {
      const splitString = _.split(transaction.labelUrl, '.com/');
      const newURL = `${SHIPPO_LABEL_URL}${splitString[1]}`;
      const response = axios.get(newURL, {
        responseType: 'arraybuffer',
        headers: {
          target: `${splitString[0]}.com/`,
        },
      });
      promise.push(response);
    });
    try {
      const data: any = await Promise.all(promise);
      const filePdf: any = [];
      data?.forEach((item: any) => {
        filePdf.push(item.data);
      });
      setFilePdf(filePdf);
    } catch (error) {
      console.error('Failed to format PDF: ', error);
    }
  };

  /**
   * Opens the shipping label creation page for the RMA ticket on screen. Navigates to the create-shipping-label route of the displayed ticket's id.
   *
   * @returns Nothing.
   */
  const handleCreateShippingLabelClick = () => {
    navigate(`/rma/create-shipping-label/${data.id}`);
  };

  /**
   * Derives everything the Outbound panel displays from the RMA ticket: one row per sender device with its label index, model, replacement, reasons, patient impact, formatted event date and delivered or pending status, plus the shipping and returning address rows. It also kicks off the label PDF download and moves any generic Other reason to the end of each device's reason list; with no ticket it yields an empty object.
   *
   * @returns Device rows, emails, notes, address rows and labels.
   */
  const formatDataOutbound = useMemo((): any => {
    if (data) {
      const rmaDevicesArray: any = [];
      const { senderInformation, recipientInformation, notificationEmail, clinicNotes } = data;
      const { devices, labels, contact: senderContact } = senderInformation;
      const { contact: recipientContact } = recipientInformation;
      formatFilePDF(labels);
      devices?.forEach((device: any) => {
        const deviceId = device.device?.deviceId;
        let labelIndex;
        labels?.forEach((label: any, index: any) => {
          const foundDevice = label.shipmentDevices
            .map((device: any) => device.deviceId)
            ?.find((id: any) => deviceId === id);
          if (foundDevice) {
            labelIndex = index + 1;
          }
        });
        /**
         * Locates the generic Other entry within a device's RMA reason list. The position is then used to move that reason to the end so it is displayed last.
         *
         * @returns Index of the Other reason, or -1.
         */
        const indexOther = device.reasons?.findIndex(
          (x: any) => x.value === DESCRIPTION_ENUM.OTHER,
        );
        if (indexOther !== -1) {
          device.reasons.push(device.reasons.splice(indexOther, 1)[0]);
        }
        rmaDevicesArray.push({
          deviceId,
          labelIndex,
          model: device.device?.model,
          replacedDevice: device.replacedDevice,
          reasons: device.reasons,
          patientImpact: device.patientImpact,
          dateOfEvent: device.dateOfEvent ? dayjs(device.dateOfEvent).format('MM/DD/YYYY') : '',
          shippingStatus: device.isReceived ? 'Delivered' : 'Pending',
          lostDeviceNeedReplacement: device.lostDeviceNeedReplacement,
        });
      });

      const shippingInfo = [
        {
          title: 'Address:',
          shippingContent: generateFullAddress({
            contact: {
              ...senderContact,
              address: senderContact.street1,
            },
            includedCountry: true,
          }),
          returningContent: generateFullAddress({
            contact: {
              ...recipientContact,
              address: recipientContact.street1,
            },
            includedCountry: true,
          }),
        },
        {
          title: 'Zip code:',
          shippingContent: senderContact.zip,
          returningContent: recipientContact.zip,
        },
        {
          title: 'Contact phone number:',
          shippingContent: senderContact.phone,
          returningContent: recipientContact.phone,
        },
        {
          title: 'Contact email:',
          shippingContent: senderContact.email,
          returningContent: recipientContact.email,
        },
      ];
      return {
        rmaDevicesArray,
        notificationEmail: notificationEmail?.split(','),
        clinicNotes,
        shippingInfo,
        labels,
      };
    }
    return {};
  }, [data]);

  const { rmaDevicesArray, notificationEmail, clinicNotes, shippingInfo, labels } =
    formatDataOutbound;

  return {
    filePdf,
    handleCreateShippingLabelClick,
    rmaDevicesArray,
    notificationEmail,
    clinicNotes,
    shippingInfo,
    labels,
  };
};

export { useTicketOutbound };
